import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const result = await login(email, password);
    if (!result.success) {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://popmenucloud.com/cdn-cgi/image/width=1200,height=1200,fit=scale-down,format=auto,quality=60/jkweqdot/f7f92250-a632-48be-b5df-753100fdb36a.jpg')`
        }}
      />
      <div className="absolute inset-0 bg-black/50" />
      
      {/* Login Card */}
      <Card className="relative z-10 w-full max-w-md mx-4 border-stone-200 shadow-2xl bg-white/95 backdrop-blur-sm rounded-2xl" data-testid="login-card">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="flex items-center justify-center mb-2">
            <img src="/grillsmith-logo-sm.png" alt="GrillSmith" className="h-36 w-auto object-contain" />
          </div>
          <p className="text-sm text-stone-500 mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Learning Management System
          </p>
        </CardHeader>
        <CardContent className="px-6 pb-8 pt-4">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700 font-medium text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@grillsmith.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border-stone-200 focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] h-11"
                data-testid="login-email-input"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-stone-700 font-medium text-sm">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#1B2A4A] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl border-stone-200 focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] h-11 pr-10"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#1B2A4A] text-white rounded-xl font-medium hover:bg-[#243658] transition-colors text-base"
              data-testid="login-submit-button"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <p className="text-center text-xs text-stone-400 mt-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Craft By Fire - Staff Training Portal
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
